import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { sportsConfig } from '../../utils/sportsConfig';
import { useSportSelection } from '../../context/SportSelectionContext';
import { selectInPlayGames } from '../../redux/selectors';
import { isComingSoonRoute, showComingSoonToast } from '../../utils/comingSoonToast';
import './sidebar.css';

const TriangleDown = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 4L6 8.5L9.5 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const TriangleUp = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 8.5L6 4L9.5 8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const SidebarMatchList = ({ sportKey }) => {
  const navigate = useNavigate();
  const selector = useMemo(() => selectInPlayGames(sportKey), [sportKey]);
  const games = useSelector(selector);
  const [expandedLeagues, setExpandedLeagues] = React.useState({});

  if (!Array.isArray(games) || games.length === 0) return null;

  const grouped = {};
  games.forEach((g) => {
    const league = g.seriesName || g.leagueName || g.competitionName || 'Other';
    if (!grouped[league]) grouped[league] = [];
    grouped[league].push(g);
  });

  const leagueEntries = Object.entries(grouped);
  const isSingleCategory = leagueEntries.length === 1;

  const toggleLeague = (league) => {
    setExpandedLeagues((prev) => ({
      ...prev,
      [league]: prev[league] === true ? false : true,
    }));
  };

  const navigateToGame = (gameEvent) => {
    const sportId = gameEvent[sportKey + 'Id'] || gameEvent.eventId;
    const gameId = gameEvent.gameId || gameEvent.eventId;
    navigate(`/game/${sportKey}/${sportId}?gameId=${gameId}`, {
      state: { gameEvent },
    });
  };
  
  if (isSingleCategory) {
    const matches = leagueEntries[0][1];
    return (
      <ul className="sidebar-match-list">
        {matches.map((match, i) => (
          <li
            key={match.eventId || match.gameId || i}
            className="sidebar-match sidebar-match--direct"
            onClick={() => navigateToGame(match)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigateToGame(match)}
          >
            {match.eventName}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="sidebar-match-list">
      {leagueEntries.map(([league, matches]) => {
        const isExpanded = expandedLeagues[league] === true;
        return (
          <li key={league} className="sidebar-league">
            <div
              className={`sidebar-league__header ${isExpanded ? 'sidebar-league__header--expanded' : ''}`}
              onClick={() => toggleLeague(league)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && toggleLeague(league)}
            >
              <span className="sidebar-league__name">{league}</span>
              <span className="sidebar-league__arrow">
                {isExpanded ? <TriangleUp /> : <TriangleDown />}
              </span>
            </div>
            {isExpanded && (
              <ul className="sidebar-league__matches">
                {matches.map((match, i) => (
                  <li
                    key={match.eventId || match.gameId || i}
                    className="sidebar-match"
                    onClick={() => navigateToGame(match)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && navigateToGame(match)}
                  >
                    {match.eventName}
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const { activeSport, setActiveSport } = useSportSelection();
  const [expandedSport, setExpandedSport] = React.useState(null);

  const handleSportClick = (sport) => {
    if (isComingSoonRoute(sport.route)) {
      showComingSoonToast();
      return;
    }
    if (sport.hasFeed) {
      if (expandedSport === sport.key) {
        setExpandedSport(null);
      } else {
        setExpandedSport(sport.key);
      }
      setActiveSport(sport.key);
    } else {
      navigate(sport.route);
      setIsSidebarOpen(false);
    }
  };

  const handleMultiMarketClick = () => {
    navigate('/multi-market');
    setIsSidebarOpen(false);
  };

  return (
    <nav
      className={`sidebar ${isSidebarOpen ? 'open' : ''}`}
      aria-label="Sports navigation"
    >
      <ul className="sidebar-sport-list">
        {/* Multi Market - always first */}
        {/* <li className="sidebar-sport-group">
          <div
            className="sidebar-sport-item"
            onClick={handleMultiMarketClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleMultiMarketClick()}
          >
            <span className="sidebar-sport-item__left">
              <span className="sidebar-sport-item__icon sidebar-sport-item__icon--multi-market">
                <FaTrophy />
              </span>
              <span className="sidebar-sport-item__name sidebar-sport-item__name--multi-market">
                Multi Market
              </span>
            </span>
          </div>
        </li> */}

        {/* Sport items */}
        {sportsConfig.map((sport) => {
          const isActive = activeSport === sport.key;
          const isExpanded = expandedSport === sport.key;

          return (
            <li key={sport.key} className="sidebar-sport-group">
              <div
                className={`sidebar-sport-item ${isActive ? 'sidebar-sport-item--active' : ''}`}
                onClick={() => handleSportClick(sport)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleSportClick(sport)}
                aria-expanded={sport.hasFeed ? isExpanded : undefined}
              >
                <span className="sidebar-sport-item__icon">
                  {sport.icon}
                </span>
                <span className="sidebar-sport-item__name">{sport.label}</span>
                <span className="sidebar-sport-item__arrow" aria-hidden="true">
                  {sport.hasFeed
                    ? (isExpanded ? <TriangleUp /> : <TriangleDown />)
                    : sport.showArrow
                      ? <TriangleDown />
                      : null}
                </span>
              </div>
              {sport.hasFeed && isExpanded && (
                <SidebarMatchList sportKey={sport.key} />
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default Sidebar;
